import type {
  CaseEventVisibility,
  CaseStage,
  CaseTimelineEventView,
  CloseReason,
  ReferralCategory,
  TexCaseView,
} from "@tex/finance-shared";
import { REFERRAL_CATEGORIES, TEX_CLOSE_REASONS } from "@tex/finance-shared";
import { useState } from "react";
import { errorMessage } from "../lib/api";
import { formatDate, humanize, STAGE_LABELS } from "../lib/format";
import {
  CLOSE_REASON_LABELS,
  REFERRAL_CATEGORY_LABELS,
  useCaseTimeline,
  useCloseCase,
} from "../lib/operations";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Mono,
  Row,
  Select,
  Textarea,
} from "./ui";

/** Mirrors TEX_CLOSABLE_STAGES in the API; the server still decides. */
const CLOSABLE_STAGES: readonly CaseStage[] = [
  "lead",
  "application",
  "review",
  "lender_review",
  "offer_review",
  "closing",
];

const VISIBILITY_LABELS: Record<CaseEventVisibility, string> = {
  tex: "Tex only",
  dealer: "Dealer",
  borrower: "Borrower",
  all: "Everyone",
};

const closeReasonLabel = (r: string) =>
  r in CLOSE_REASON_LABELS ? CLOSE_REASON_LABELS[r as CloseReason] : humanize(r);
const referralLabel = (c: string) =>
  c in REFERRAL_CATEGORY_LABELS
    ? REFERRAL_CATEGORY_LABELS[c as ReferralCategory]
    : humanize(c);

export function CaseActivityTab({ view }: { view: TexCaseView }) {
  return (
    <div className="space-y-6">
      <ClosePanel view={view} />
      <TimelineCard caseId={view.id} audience="tex" />
    </div>
  );
}

function ClosePanel({ view }: { view: TexCaseView }) {
  if (view.stage === "closed") {
    return (
      <Card title="Closed">
        <dl>
          <Row
            label="Reason"
            value={view.close_reason ? closeReasonLabel(view.close_reason) : null}
          />
          {view.referral_category && (
            <Row
              label="Referral"
              value={referralLabel(view.referral_category)}
            />
          )}
          {view.referred_to && (
            <Row label="Referred to" value={view.referred_to} />
          )}
        </dl>
      </Card>
    );
  }
  if (view.stage === "funded") {
    return (
      <Alert tone="info">
        A funded case is finished; it is not closed by hand.
      </Alert>
    );
  }
  if (!CLOSABLE_STAGES.includes(view.stage)) return null;
  return (
    <Card title="Close this case">
      <p className="mb-3 text-sm text-neutral-500">
        Closing ends the file for everyone on it. The borrower and dealer are
        told the status only — never the reason. A request that is not a
        purchase Tex arranges (cash against owned equipment, receivables,
        private-party sales…) is closed as{" "}
        <em>out of scope, referred</em> so the funnel counts what we turned
        away and where it went.
      </p>
      <CloseControl view={view} />
    </Card>
  );
}

function CloseControl({ view }: { view: TexCaseView }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<CloseReason>("no_offer");
  const [category, setCategory] = useState<ReferralCategory>("cash_out");
  const [referredTo, setReferredTo] = useState("");
  const [note, setNote] = useState("");
  const close = useCloseCase(view.id);
  const referral = reason === "out_of_scope_referred";

  if (!open) {
    return (
      <Button type="button" variant="danger" onClick={() => setOpen(true)}>
        Close case
      </Button>
    );
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        close.mutate(
          {
            expected_version: view.version,
            close_reason: reason,
            referral_category: referral ? category : null,
            referred_to: referral && referredTo.trim() ? referredTo.trim() : null,
            note: note.trim(),
          },
          { onSuccess: () => setOpen(false) },
        );
      }}
      className="max-w-xl space-y-3 rounded-md border border-red-200 bg-red-50 p-3"
    >
      <Field label="Reason">
        <Select
          value={reason}
          onChange={(e) => setReason(e.target.value as CloseReason)}
          disabled={close.isPending}
        >
          {TEX_CLOSE_REASONS.map((r) => (
            <option key={r} value={r}>
              {CLOSE_REASON_LABELS[r]}
            </option>
          ))}
        </Select>
      </Field>
      {referral && (
        <>
          <Field
            label="What did the buyer actually need?"
            hint="Required for a referral; counted in the funnel report."
          >
            <Select
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as ReferralCategory)
              }
              disabled={close.isPending}
            >
              {REFERRAL_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {REFERRAL_CATEGORY_LABELS[c]}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Referred to"
            hint="Optional: the firm or specialist the buyer was pointed at."
          >
            <Input
              value={referredTo}
              onChange={(e) => setReferredTo(e.target.value)}
              maxLength={200}
              disabled={close.isPending}
            />
          </Field>
        </>
      )}
      <Field
        label="Note"
        hint="Internal: recorded in the audit trail, never shown to the borrower or dealer."
      >
        <Textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          required
          minLength={2}
          maxLength={2000}
          disabled={close.isPending}
        />
      </Field>
      {close.isError && <Alert tone="error">{errorMessage(close.error)}</Alert>}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setOpen(false)}
          disabled={close.isPending}
        >
          Keep it open
        </Button>
        <Button
          type="submit"
          variant="danger"
          disabled={close.isPending || note.trim().length < 2}
        >
          {close.isPending ? "Closing…" : "Close case"}
        </Button>
      </div>
    </form>
  );
}

/** The case timeline as the server projects it for `audience`; Tex additionally sees visibility and metadata. */
export function TimelineCard({
  caseId,
  audience,
}: {
  caseId: string;
  audience: "tex" | "dealer" | "borrower";
}) {
  const timeline = useCaseTimeline(caseId);
  const events = timeline.data?.pages.flatMap((p) => p.events) ?? [];
  return (
    <Card title="Activity">
      {timeline.isPending && (
        <p className="text-sm text-neutral-500">Loading…</p>
      )}
      {timeline.isError && (
        <Alert tone="error">{errorMessage(timeline.error)}</Alert>
      )}
      {timeline.data && events.length === 0 && (
        <p className="text-sm text-neutral-500">Nothing recorded yet.</p>
      )}
      {events.length > 0 && (
        <ol className="divide-y divide-neutral-100">
          {events.map((e) => (
            <TimelineRow key={e.id} event={e} tex={audience === "tex"} />
          ))}
        </ol>
      )}
      {timeline.hasNextPage && (
        <div className="mt-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void timeline.fetchNextPage()}
            disabled={timeline.isFetchingNextPage}
          >
            {timeline.isFetchingNextPage ? "Loading…" : "Show earlier"}
          </Button>
        </div>
      )}
    </Card>
  );
}

function TimelineRow({
  event,
  tex,
}: {
  event: CaseTimelineEventView;
  tex: boolean;
}) {
  const [showMeta, setShowMeta] = useState(false);
  const metaKeys = event.metadata ? Object.keys(event.metadata) : [];
  return (
    <li className="py-2 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="font-medium text-neutral-900">{event.summary}</span>
        <span className="text-xs text-neutral-500">
          {formatDate(event.created_at)}
        </span>
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500">
        <Mono>{event.type}</Mono>
        <span>by {humanize(event.actor_type)}</span>
        {event.to_stage && (
          <span>
            {event.from_stage ? `${STAGE_LABELS[event.from_stage]} → ` : ""}
            {STAGE_LABELS[event.to_stage]}
          </span>
        )}
        {tex && (
          <span className="rounded bg-neutral-100 px-1.5 py-0.5">
            {VISIBILITY_LABELS[event.visibility]}
          </span>
        )}
        {tex && metaKeys.length > 0 && (
          <button
            type="button"
            className="text-tex-blue hover:underline"
            onClick={() => setShowMeta((v) => !v)}
          >
            {showMeta ? "Hide details" : "Details"}
          </button>
        )}
      </div>
      {showMeta && event.metadata && (
        <pre className="mt-2 overflow-x-auto rounded bg-neutral-50 p-2 text-xs text-neutral-700">
          {JSON.stringify(event.metadata, null, 2)}
        </pre>
      )}
    </li>
  );
}
